import { Button } from "$components/button/button";
import { PrinterIcon } from "$components/icons";
import { FeatureFlagGuard } from "$global-providers/feature-flags-provider/feature-flag-guard";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import type { Vmp } from "$global-providers/rxdb-provider/collections/vmp";
import { toast } from "$global-providers/toast-provider";
import { PrescriptionHistoryCard } from "$routes/patient/_lib/prescription-history-card";
import { ConsultationHistoryCard } from "$routes/patient/[:patientId]/_lib/consultation-history-card";
import InvestigationActivityOutcome from "$routes/patient/[:patientId]/_lib/investigations/investigation-activity-outcome";
import {
  isPatientHisoryFile as isPatientHistoryFile,
  PatientHistoryFileCard,
} from "$routes/patient/[:patientId]/_lib/patient-history-file-upload";
import ThermalPrintableArea from "$routes/patient/[:patientId]/_lib/printing/thermal-printable-area";
import useThermalPrintDivAsImage from "$routes/patient/[:patientId]/_lib/printing/use-thermal-print-div-as-image";
import { ReferralHistoryCard } from "$routes/patient/[:patientId]/_lib/referral-history-card";
import { VaccinationHistoryCard } from "$routes/patient/[:patientId]/_lib/vaccination-history-card";
import AncEncounterCard from "$routes/patient/[:patientId]/antenatal-care/_lib/anc-encounter-card";
import { type HistoricAncEncounter } from "$routes/patient/[:patientId]/antenatal-care/_lib/anc-encounters";
import { AncPrescriptionCard } from "$routes/patient/[:patientId]/antenatal-care/_lib/prescription/anc-prescription-card";
import InpatientCard from "$routes/patient/[:patientId]/inpatient/_lib/activity-card/inpatient-card";
import { InvestigationActivityOutcome as V2InvestigationActivityOutcome } from "$routes/patient/[:patientId]/investigations/_lib/investigation-activity-outcome";
import { EpocusHistoryCard } from "$routes/patient/[:patientId]/overview/history/_lib/epocus-history-card";
import { ImagingStudyCard } from "$routes/patient/[:patientId]/overview/history/_lib/imaging-study-card";
import { Box, styled } from "@material-ui/core";
import { AdministrationApproach, type Gender } from "elephant-web/gql";
import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DownloadReportButton } from "./download-report-button";
import { HemeHealthCard } from "./heme-health-card";
import PrescriptionInstructions from "./prescription-instructions";
import Investigation from "./sections/investigations";
import {
  type HistoricPatientEvent,
  isAncEncounter,
  isConsultation,
  isEpocusEncounter,
  isImagingStudy,
  isInvestigationActivity,
  isLegacyTestResult,
  isReferral,
  isRxInvestigationActivity,
  isRxPrescription,
  isVaccination,
  isVisit,
} from "./types";

export type GroupedHistoricEvents = Record<string, HistoricPatientEvent[]>;

const ActionButtonsWrapper = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  // Needed while the following feature featue flag exists, to ensure the
  // 'Download Reports' button stays on the right when print button not shown.
  // Delete if rollout.patient.history.thermal-print-medication-instructions is deleted.
  "& > div:last-child": {
    marginLeft: "auto",
  },
});

export const DateHeader = styled("span")(({ theme }) => ({
  display: "inline-block",
  fontSize: "14px",
  fontWeight: 700,
  background: theme.palette.grey[300],
  padding: "5px 8px",
  borderRadius: "3px",
  width: "max-content",
}));

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const PrintMedicationInstructionsButton = ({
  print,
  printableRef,
  patientName,
  facilityName,
  prescriptionInstructions,
}: {
  print: () => Promise<void>;
  printableRef: React.MutableRefObject<HTMLDivElement>;
  patientName: string;
  facilityName: string;
  prescriptionInstructions: React.ComponentProps<
    typeof PrescriptionInstructions
  >["instructions"];
}) => (
  <>
    <Button color="secondary" onClick={print} startIcon={<PrinterIcon />}>
      <FormattedMessage
        id="patient.history.printMedicationInstructions"
        defaultMessage="Print Medication Instructions"
      />
    </Button>
    <ThermalPrintableArea printableRef={printableRef}>
      <PrescriptionInstructions
        patientName={patientName}
        facilityName={facilityName}
        instructions={prescriptionInstructions}
      />
    </ThermalPrintableArea>
  </>
);

export const HistoricEventsForDate: FC<{
  date: string;
  patient?: {
    name: string;
    dob: Date;
    cardId: string;
    uuid: string;
    gender: Gender;
    phoneNumber?: string;
  };
  historicEvents: HistoricPatientEvent[];
  prescriptionVmps: Array<Vmp>;
  facilityName: string;
}> = ({ date, historicEvents, facilityName, patient, prescriptionVmps }) => {
  const { formatMessage } = useIntl();
  const { print, printableRef, error } = useThermalPrintDivAsImage();
  const [hemeHealthIntegrationPatientId] = useFeatureFlag<string>(
    featureFlags.fakeHemeHealthIntegration,
  );

  const [vaccinationsFlag] = useFeatureFlag(
    featureFlags.immunisations.addVaccineFlow,
  );
  const [imagingStudySyncFlag] = useFeatureFlag(
    featureFlags.pocus.imagingStudySync,
  );

  useEffect(() => {
    if (!error) {
      return;
    }
    toast.error(
      formatMessage(
        {
          id: "investigationActivity.outcome.printer.error",
          defaultMessage: "A printer error occured: {message}",
        },
        { message: error.message },
      ),
    );
  }, [error, formatMessage]);

  const rxOpdPrescriptionInstructions: React.ComponentProps<
    typeof PrescriptionInstructions
  >["instructions"] = historicEvents
    .filter((event) => isRxPrescription(event))
    .map((prescription) => {
      const title =
        prescriptionVmps.find(
          (vmp) => vmp.id === prescription.clinicalProduct?.id,
        )?.title ??
        prescription.prescribableItemFreeText ??
        "";

      return {
        id: prescription.id,
        title,
        prescribedAt: new Date(prescription.meta.created),
        notes: prescription.note,
        dose: {
          asNeeded:
            prescription.administrationApproach ===
            AdministrationApproach.AsRequired,
          frequency: prescription.rate,
          duration: prescription.frequency,
        },
      };
    });

  const ancPrescriptionInstructions: React.ComponentProps<
    typeof PrescriptionInstructions
  >["instructions"] = historicEvents
    .filter((event) => isAncEncounter(event) && !!event.prescriptions)
    .flatMap((event: HistoricAncEncounter) => {
      return event.prescriptions.map((prescription) => ({
        id: prescription.id,
        title: prescription.clinicalProduct?.title ?? "",
        prescribedAt: new Date(event.meta.created),
        notes: prescription.note,
        dose: {
          asNeeded: prescription.asRequired,
          frequency: prescription.rate,
          duration: prescription.frequency,
        },
      }));
    });

  const prescriptionInstructions = [
    ...rxOpdPrescriptionInstructions,
    ...ancPrescriptionInstructions,
  ];

  return (
    <Container>
      <Box display="flex" justifyContent="space-between">
        <DateHeader>{date}</DateHeader>
      </Box>
      {patient.uuid === hemeHealthIntegrationPatientId ? (
        <HemeHealthCard />
      ) : null}
      {historicEvents.map((historicEvent) => {
        if (isAncEncounter(historicEvent)) {
          return (
            <Container key={historicEvent.id}>
              <AncEncounterCard encounter={historicEvent} />
              {!!historicEvent?.prescriptions?.length &&
                historicEvent.prescriptions.map((p) => (
                  <AncPrescriptionCard key={p.id} prescription={p} />
                ))}
            </Container>
          );
        }
        if (isInvestigationActivity(historicEvent)) {
          return (
            <InvestigationActivityOutcome
              key={historicEvent._id}
              // TODO: THB-653
              investigation={historicEvent}
              subtitle={formatMessage({
                id: "investigationActivity.outcome.header",
                defaultMessage: "Investigation",
              })}
              patientUuid={patient?.uuid}
            />
          );
        }

        if (isRxInvestigationActivity(historicEvent)) {
          return (
            <V2InvestigationActivityOutcome
              key={historicEvent.id}
              investigationActivity={historicEvent}
            />
          );
        }

        // TestResults (legacy investigations)
        if (isLegacyTestResult(historicEvent)) {
          return (
            <Investigation key={historicEvent._id} testResult={historicEvent} />
          );
        }

        // Inpatient visits (detention-only visits filtered out above)
        if (isVisit(historicEvent)) {
          const { admissionEvent, dischargeEvent } = historicEvent;

          return (
            <InpatientCard
              key={historicEvent._id}
              patientUuid={patient?.uuid}
              visitId={historicEvent._id}
              admissionEvent={admissionEvent}
              dischargeEvent={dischargeEvent}
              data-testid="patient-details-inpatient-visit"
            />
          );
        }

        if (isRxPrescription(historicEvent)) {
          const dispensation = historicEvent.dispensations[0];
          return (
            <PrescriptionHistoryCard
              key={historicEvent.id}
              prescription={{
                ...historicEvent,
                rate: historicEvent.rate,
                dose: historicEvent.dosage,
                duration: historicEvent.frequency,
                clinicalProductId: historicEvent.clinicalProduct?.id ?? null,
                clinicalProductType: historicEvent.clinicalProduct?.type,
                clinicalProductFreetext: historicEvent.prescribableItemFreeText,
                createdAt: historicEvent.meta.createdAt,
              }}
              dispensation={
                dispensation
                  ? {
                      createdAt: dispensation.meta.createdAt,
                      dispensationStrategy: dispensation.dispensationStrategy,
                      dispensedByUserId: dispensation.dispensedByUserId,
                      skuId: dispensation.skuId,
                      quantity: dispensation.quantity,
                    }
                  : undefined
              }
            />
          );
        }

        if (isPatientHistoryFile(historicEvent)) {
          return (
            <PatientHistoryFileCard
              fileName={historicEvent.fileName}
              filePath={historicEvent.filePath}
            />
          );
        }

        if (vaccinationsFlag && isVaccination(historicEvent)) {
          return <VaccinationHistoryCard consultation={historicEvent} />;
        }

        if (imagingStudySyncFlag && isImagingStudy(historicEvent)) {
          return (
            <ImagingStudyCard key={historicEvent.id} study={historicEvent} />
          );
        }

        if (isEpocusEncounter(historicEvent)) {
          return (
            <EpocusHistoryCard
              key={historicEvent.id}
              encounter={historicEvent}
            />
          );
        }

        if (isReferral(historicEvent)) {
          return (
            <ReferralHistoryCard
              key={historicEvent.id}
              referral={historicEvent}
            />
          );
        }

        if (isConsultation(historicEvent)) {
          const deliveredBy = historicEvent.deliveredBy?.person
            ? [
                historicEvent.deliveredBy.person.firstname,
                historicEvent.deliveredBy.person.lastname,
              ]
                .filter(Boolean)
                .join(" ")
            : undefined;
          return (
            <ConsultationHistoryCard
              key={historicEvent.uuid}
              diagnoses={historicEvent.diagnoses}
              diseases={historicEvent.diseases}
              problem={historicEvent.problem}
              presentingComplaint={historicEvent.presentingComplaint}
              symptoms={historicEvent.symptoms}
              plan={historicEvent.plan}
              deliveredBy={deliveredBy}
              deliveredOn={new Date(historicEvent.meta.created)}
              locationName={historicEvent.location?.name}
              consultationId={historicEvent.uuid}
            />
          );
        }
      })}

      {patient != null && (
        <ActionButtonsWrapper>
          <FeatureFlagGuard
            flagKey={
              featureFlags.patient.history.thermalPrintMedicationInstructions
            }
            render={() =>
              prescriptionInstructions.length > 0 && (
                <PrintMedicationInstructionsButton
                  print={print}
                  printableRef={printableRef}
                  patientName={patient?.name ?? ""}
                  facilityName={facilityName}
                  prescriptionInstructions={prescriptionInstructions}
                />
              )
            }
          />
          <DownloadReportButton
            date={date}
            historicEvents={historicEvents}
            referrals={historicEvents.filter(isReferral)}
            patient={patient}
            clinicName={facilityName}
          />
        </ActionButtonsWrapper>
      )}
    </Container>
  );
};
